/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { EventEmitter, Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';

@Injectable()
export class TableService {
    /**
     * To store and access the visible column amount to show and hide the hide icon.
     */
    public visibleColumnLength: number;

    /**
     * To store the dropdown values for the pagination.
     */
    public dropdownValues: any;

    /**
     * To store the current page number for the pagination.
     */
    public currentPage: number = 1;

    /**
     * To store the page numbers to be looped through in the view.
     */
    public pageNumbers: any[] = [];

    /**
     * A boolean to determine if the left ellipsis should be shown or not.
     */
    public showLeftEllipsis: boolean = false;

    /**
     * A boolean to determine if the right ellipsis should be shown or not.
     */
    public showRightEllipsis: boolean = false;

    /**
     * A subject emitter for the amount shown.
     */
    public amountShown: Subject<number> = new Subject<number>();

    /**
     * A subject emitter for the goto page input value.
     */
    public goToPage: Subject<number> = new Subject<number>();

    /**
     * Store remaining width available.
     */
    public widthAvailable: number = 100;

    /**
     * Initialise paginator.
     */
    public paginatorInitialised: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Store the datasources for the tables.
     */
    public dataSources: MatTableDataSource<any>[] = [];

    /**
     * Store datasource for the tables in a lookup.
     */
    public dataSourceLookup: any = {};

    /**
     * Store the selected rows for getter/setter.
     * @private
     */
    private _selectedRows: any[];

    /**
     * Store container width.
     * @private
     */
    private _containerWidth: any;

    /**
     * Store column width.
     * @private
     */
    private _columnWidth: any;

    /**
     * Store column count.
     * @private
     */
    private _columnCount: number;

    /**
     * Setter/getter for selected rows.
     * @param rows
     */
    set selectedRows(rows: any[]) {
        this._selectedRows = rows;
    }

    get selectedRows() {
        return this._selectedRows;
    }

    /**
     * Setter/getter for container width.
     * @param width
     */
    set containerWidth(width: any) {
        this._containerWidth = width;
    }

    get containerWidth() {
        return this._containerWidth;
    }

    /**
     * Setter/getter for column width.
     * @param width
     */
    set columnWidth(width: any) {
        this._columnWidth = width;
    }

    get columnWidth() {
        return this._columnWidth;
    }

    /**
     * Set/getter column count.
     * @param column
     */
    set columnCount(column: number) {
        this._columnCount = column;
    }

    get columnCount() {
        return this._columnCount;
    }

    /**
     * Get the column widths based on the column mode.
     * @param options
     */
    public calculateWidthFromMode(options: any): any {
        if (options.columnMode === 'force') {
            return 100 / options.columns.length;
        } else if (options.columnMode === 'standard') {
            const standardWidthData = [];
            for (const column of options.columns) {
                standardWidthData.push(column.columnWidth);
            }

            return standardWidthData;
        }
    }

    /**
     * Dynamically set the inline style.
     * @param widthData
     * @param column
     * @param unit
     */
    public getStyle(widthData: any, column: any, unit: string): any {
        const style = {
            'display': column.columnHidden === false ? 'flex' : 'none',
        };

        if (column.flexGrow) {
            style['flex-grow'] = column.flexGrow;
            style['flex-basis'] = 0;
        } else if (column.columnWidth) {
            style['width'] = column.columnWidth + unit;
        } else {
            style['width'] = widthData + unit;
        }

        return style;
    }

    /**
     * Filter the hidden columns to get the length of visible columns.
     * @param hiddenColumnsArray
     */
    public setVisibleColumnLength(hiddenColumnsArray: any[]): void {
        this.visibleColumnLength = hiddenColumnsArray.filter(value => value === false).length;
    }

    /**
     * Get start and end indices for current page.
     * @param dataSource
     */
    public getIndicesForCurrentPage(dataSource: any): any {
        const { pageIndex, pageSize } = dataSource?.paginator;
        const dataLength: number = dataSource?.filteredData?.length;

        const startIndex: number = pageSize * pageIndex;
        let endIndex: number = startIndex + (pageSize - 1);

        if (endIndex >= dataLength) {
            endIndex = dataLength;
        }

        return { startIndex, endIndex, pageIndex };
    }

    /**
     * Remove the datasource from the collection.
     * @param id
     */
    public removeTableFromLookup(id: string): void {
        delete this.dataSourceLookup[id];
    }
}
